import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/app/StatusBadge";
import { ClipboardList, ScanLine, CheckCircle2, X } from "lucide-react";

export default function InventoryAudit() {
  const [items, setItems] = useState([]);
  const [masters, setMasters] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "cycle_count", scope_master_company_id: "" });
  const [active, setActive] = useState(null);
  const [scanInput, setScanInput] = useState("");

  const load = () => { api.get("/inventory-audits").then(({ data }) => setItems(data.items || [])); };
  useEffect(() => {
    load();
    api.get("/master-companies").then(({ data }) => setMasters(data.items || []));
  }, []);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    try { const { data } = await api.post("/inventory-audits", form); toast.success(`Audit ${data.audit_code} created`); setForm({ ...form, name: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const scan = async () => {
    if (!active || !scanInput.trim()) return;
    try {
      const { data } = await api.post(`/inventory-audits/${active.id}/scan`, { asset_id: scanInput.trim(), verified: true });
      toast.success(`Verified ${data.verified} / Unexpected ${data.unexpected}`);
      setScanInput("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const close = async (id) => {
    try { const { data } = await api.post(`/inventory-audits/${id}/close`); toast.success(`Closed. Verified ${data.verified}, missing ${data.missing}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-[1600px]">
      <div className="lg:col-span-2 space-y-4">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Asset Intelligence</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Inventory Audit</h1>
          <p className="text-sm text-slate-500 mt-0.5">Formal stock takes with expected/verified/missing/unexpected reconciliation.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Kind</th>
                <th className="px-3 py-2 text-left">Expected</th>
                <th className="px-3 py-2 text-left">Verified</th>
                <th className="px-3 py-2 text-left">Missing</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{a.audit_code}</td>
                  <td className="px-3 py-2">{a.name}</td>
                  <td className="px-3 py-2 capitalize text-xs">{a.kind.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 tabular-nums">{a.expected_asset_ids?.length || 0}</td>
                  <td className="px-3 py-2 tabular-nums">{a.verified_asset_ids?.length || 0}</td>
                  <td className="px-3 py-2 tabular-nums">{a.missing_asset_ids?.length || 0}</td>
                  <td className="px-3 py-2"><StatusBadge value={a.status} /></td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5">
                      {a.status === "in_progress" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setActive(a)}><ScanLine className="h-4 w-4 mr-1" /> Scan</Button>
                          <Button size="sm" variant="ghost" onClick={() => close(a.id)}><CheckCircle2 className="h-4 w-4 mr-1" /> Close</Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 mb-2">
            <ClipboardList className="h-3 w-3" /> New audit
          </div>
          <div className="space-y-2">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Audit name" className="h-9" />
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["full", "cycle_count", "customer", "warehouse", "location", "random"].map((k) => <SelectItem key={k} value={k}><span className="capitalize">{k.replace(/_/g, " ")}</span></SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.scope_master_company_id} onValueChange={(v) => setForm({ ...form, scope_master_company_id: v })}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Scope master co" /></SelectTrigger>
              <SelectContent>{masters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="w-full h-9 bg-slate-900 text-white" onClick={create}>Create audit</Button>
          </div>
        </div>

        {active && (
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Scanning: {active.audit_code}</div>
            <div className="flex gap-2">
              <Input value={scanInput} onChange={(e) => setScanInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scan()} placeholder="Asset ID / scan…" className="h-9" />
              <Button onClick={scan} size="sm" className="bg-slate-900 text-white">Add</Button>
            </div>
            <Button variant="ghost" className="mt-2 text-xs" onClick={() => setActive(null)}><X className="h-3.5 w-3.5 mr-1" /> Close</Button>
          </div>
        )}
      </div>
    </div>
  );
}
